import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { useTheme } from "@mui/styles";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  useMediaQuery,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import JobOfferList from "bundles/Candidate/components/JobOfferList";
import { ENGINEERING_CATEGORIES } from "bundles/common/utils/categoryData.js";
import BreadcrumbsBar from "./BreadcrumbsBar";

const EngineeringJobs = () => {
  const i18nLocale = useSelector((state) => state.i18nLocale) || "en";
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [jobOffers, setJobOffers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [categoryJobs, setCategoryJobs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    sh.get(`/${i18nLocale}/job-offers/engineering/fetch`, {
      params: {
        authenticity_token: authenticityToken,
        page,
        industry: "engineering",
      },
    })
      .then((res) => {
        const offers = res.data?.data || [];
        setJobOffers(offers);
        setPageCount(res.data?.pagy?.pages || 1);

        if (!selectedJob && offers.length > 0) {
          setSelectedJob(offers[0]);
        }

        const categorizedJobs = {};
        offers.forEach(offer => {
          (offer.categories || []).forEach(slug => {
            (categorizedJobs[slug] ||= []).push(offer);
          });
        });
        setCategoryJobs(categorizedJobs);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching job offers:", err);
        setLoading(false);
      });
  }, [page, i18nLocale, authenticityToken, selectedJob]);

  const getCategoryHref = slugOrSlugs =>
    `/${i18nLocale}/job-offers/engineering/${[]
      .concat(slugOrSlugs)
      .filter(Boolean)
      .join(",")
    }`;

  const categories =
    ENGINEERING_CATEGORIES[i18nLocale] || ENGINEERING_CATEGORIES["en"];

  const crumbs = [
    { label: "Home", path: `/${i18nLocale}` },
    { label: "Job Offers", path: `/${i18nLocale}/job-offers` },
    {
      label: "Engineering",
      path: `/${i18nLocale}/job-offers/engineering`,
      current: true,
    },
  ];

  return (
    <Container maxWidth="lg" style={{ marginTop: 40 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h1">
          <FormattedMessage id="public_job_offer_index.engineering_index_header" />
        </Typography>
        <Typography variant="subtitle1">
          <FormattedMessage id="public_job_offer_index.engineering_index_subtitle" />
        </Typography>
      </Box>

      <Container maxWidth="lg">
        <BreadcrumbsBar crumbs={crumbs} />
      </Container>

      {/* Category Buttons */}
      <Container maxWidth="md">
        <Grid container spacing={2} mt={2} mb={4} justifyContent="center">
          {categories.map((cat) => {
            const linkHref = getCategoryHref(cat.slug);
            return (
              <Grid item xs={6} md={3} key={cat.slug}>
                <a
                  href={linkHref}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ p: 2 }}
                    disabled={loading}
                  >
                    {cat.label}
                  </Button>
                </a>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Job Listings */}
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <JobOfferList
            offers={jobOffers}
            activeOffer={selectedJob}
            handleOfferClick={offer =>
              navigate(`/${i18nLocale}/job-offers/${offer.id}`, { state: { crumbs } })
            }
            columnPerRow={isDesktop ? 2 : 1}
            page={page}
            setPage={setPage}
            pageCount={pageCount}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default EngineeringJobs;
